import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowRight, MapPin, Calendar, ExternalLink, Users, ChevronLeft, ChevronRight, X, Link as LinkIcon, LayoutGrid, CalendarDays, CalendarPlus } from "lucide-react";
import { useLang } from "../lib/i18n";
import { EVENT_TYPES, EVENT_TYPE_META, typeLabel, typeIcon, typeTone, formatDate, formatDateTime, isPast } from "../lib/events";
import EventsCalendar from "../components/EventsCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Events() {
  const { t, lang } = useLang();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'calendar'

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/events`)
      .then(({ data }) => {
        if (!cancelled) setEvents(data || []);
      })
      .catch((e) => console.warn("Failed to load events", e?.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const featured = useMemo(() => events.filter((e) => e.featured && !isPast(e.date)), [events]);
  const list = useMemo(() => {
    let arr = events.filter((e) => (showPast ? isPast(e.date) : !isPast(e.date)));
    if (filter !== "all") arr = arr.filter((e) => e.type === filter);
    if (showPast) arr = [...arr].reverse(); // most recent past first
    return arr;
  }, [events, filter, showPast]);

  const i18n = lang === "tr" ? TR : EN;

  return (
    <div data-testid="events-page">
      {/* Hero */}
      <section className="container-x pt-12 pb-10 md:pt-20 md:pb-14">
        <span className="label-eyebrow">{i18n.eyebrow}</span>
        <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink max-w-4xl">
          {i18n.title}
        </h1>
        <p className="mt-6 text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-2xl">
          {i18n.lead}
        </p>
      </section>

      {/* Featured slider */}
      {featured.length > 0 && (
        <section className="container-x pb-12 md:pb-16">
          <FeaturedSlider items={featured} lang={lang} onOpen={setSelected} i18n={i18n} />
        </section>
      )}

      {/* Filters */}
      <section className="container-x">
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="events-filters">
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
            {i18n.all}
          </FilterPill>
          {EVENT_TYPES.map((tp) => (
            <FilterPill key={tp} active={filter === tp} onClick={() => setFilter(tp)} testid={`filter-${tp}`}>
              {typeLabel(tp, lang)}
            </FilterPill>
          ))}
          <div className="flex-1" />
          <div className="inline-flex rounded-full border border-ink/15 p-0.5">
            <button
              data-testid="view-list"
              onClick={() => setViewMode("list")}
              className={`text-xs uppercase tracking-[0.22em] px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "list" ? "bg-ink text-cream-100" : "text-ink/55 hover:text-ink"
              }`}
            >
              <LayoutGrid className="w-3 h-3" strokeWidth={1.6} />
              {i18n.list}
            </button>
            <button
              data-testid="view-calendar"
              onClick={() => setViewMode("calendar")}
              className={`text-xs uppercase tracking-[0.22em] px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                viewMode === "calendar" ? "bg-ink text-cream-100" : "text-ink/55 hover:text-ink"
              }`}
            >
              <CalendarDays className="w-3 h-3" strokeWidth={1.6} />
              {i18n.calendar}
            </button>
          </div>
          {viewMode === "list" && (
            <button
              data-testid="toggle-past"
              onClick={() => setShowPast((p) => !p)}
              className={`text-xs uppercase tracking-[0.22em] px-4 py-2 rounded-full border transition-colors ${
                showPast ? "bg-ink text-cream-100 border-ink" : "border-ink/15 text-ink/65 hover:text-ink"
              }`}
            >
              {showPast ? i18n.upcoming : i18n.past}
            </button>
          )}
        </div>
      </section>

      {/* Grid / Calendar */}
      <section className="container-x pb-24 md:pb-32">
        {loading ? (
          <div className="text-ink/55 text-sm py-16 text-center">{i18n.loading}</div>
        ) : viewMode === "calendar" ? (
          <EventsCalendar
            events={events.filter((e) => (filter === "all" ? true : e.type === filter))}
            lang={lang}
            onOpen={setSelected}
          />
        ) : list.length === 0 ? (
          <div className="surface-card p-12 text-center">
            <p className="font-serif text-2xl text-ink mb-2">{i18n.emptyTitle}</p>
            <p className="text-sm text-ink/60">{i18n.emptyBody}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map((ev) => (
              <EventCard key={ev.id} ev={ev} lang={lang} onOpen={() => setSelected(ev)} i18n={i18n} />
            ))}
          </div>
        )}
      </section>

      {selected && <EventDetailModal ev={selected} lang={lang} onClose={() => setSelected(null)} i18n={i18n} />}
    </div>
  );
}

/* ---------- subcomponents ---------- */

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-xs uppercase tracking-[0.22em] px-4 py-2 rounded-full border transition-colors ${
        active ? "bg-ink text-cream-100 border-ink" : "border-ink/15 text-ink/65 hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function pickLang(field, lang, fb = "") {
  if (!field) return fb;
  if (typeof field === "string") return field;
  return field[lang] || field.tr || field.en || fb;
}

function EventCard({ ev, lang, onOpen, i18n }) {
  const Icon = typeIcon(ev.type);
  const past = isPast(ev.date);
  return (
    <button
      data-testid={`event-card-${ev.id}`}
      onClick={onOpen}
      className="group surface-card overflow-hidden text-left flex flex-col hover:-translate-y-1 hover:shadow-float transition-all duration-500"
    >
      <div className="aspect-[16/10] overflow-hidden bg-cream-200 relative">
        {ev.image ? (
          <img src={ev.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-cream-300">
            <Icon className="w-12 h-12" strokeWidth={1} />
          </div>
        )}
        <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-medium px-2.5 py-1 rounded-full ${typeTone(ev.type)}`}>
          <Icon className="w-3 h-3" strokeWidth={1.6} />
          {typeLabel(ev.type, lang)}
        </span>
        {past && (
          <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] bg-ink/75 text-cream-100 px-2.5 py-1 rounded-full">
            {i18n.pastBadge}
          </span>
        )}
      </div>
      <div className="p-5 md:p-6 flex flex-col flex-1">
        <p className="text-xs text-ink/55 flex items-center gap-1.5">
          <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
          {formatDateTime(ev.date, lang)}
        </p>
        <h3 className="mt-2 font-serif text-2xl text-ink leading-tight">{pickLang(ev.title, lang)}</h3>
        {pickLang(ev.description, lang) && (
          <p className="mt-3 text-sm font-light text-ink/65 line-clamp-3 leading-relaxed">{pickLang(ev.description, lang)}</p>
        )}
        {(ev.location || ev.price) && (
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink/55">
            {pickLang(ev.location, lang) && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" strokeWidth={1.5} />
                {pickLang(ev.location, lang)}
              </span>
            )}
            {pickLang(ev.price, lang) && (
              <span className="font-medium text-terracotta-400">{pickLang(ev.price, lang)}</span>
            )}
          </div>
        )}
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-ink/80 group-hover:text-terracotta-400 transition-colors">
          {i18n.viewDetails}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
        </span>
      </div>
    </button>
  );
}

function FeaturedSlider({ items, lang, onOpen, i18n }) {
  const [idx, setIdx] = useState(0);
  const count = items.length;
  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % count), 6000);
    return () => clearInterval(id);
  }, [count]);

  const go = (n) => setIdx((i) => (i + n + count) % count);
  const ev = items[idx];
  const Icon = typeIcon(ev.type);

  return (
    <div data-testid="featured-slider" className="relative rounded-3xl overflow-hidden shadow-float">
      <div className="grid lg:grid-cols-2 min-h-[420px] bg-ink text-cream-100">
        <div className="relative">
          {ev.image ? (
            <img src={ev.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-sage-700 flex items-center justify-center"><Icon className="w-20 h-20 text-cream-100/30" strokeWidth={1} /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-ink/40 lg:from-ink/0 to-transparent" />
        </div>
        <div className="p-8 md:p-12 lg:p-14 flex flex-col justify-center">
          <span className={`self-start inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full ${typeTone(ev.type)}`}>
            <Icon className="w-3 h-3" strokeWidth={1.6} />
            {typeLabel(ev.type, lang)}
          </span>
          <h2 className="mt-5 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight">{pickLang(ev.title, lang)}</h2>
          {pickLang(ev.description, lang) && (
            <p className="mt-5 text-base md:text-lg text-cream-100/75 font-light max-w-xl line-clamp-3">{pickLang(ev.description, lang)}</p>
          )}
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-cream-100/80">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" strokeWidth={1.5} />
              {formatDateTime(ev.date, lang)}
            </span>
            {pickLang(ev.location, lang) && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
                {pickLang(ev.location, lang)}
              </span>
            )}
          </div>
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <button
              data-testid="featured-open-details"
              onClick={() => onOpen(ev)}
              className="inline-flex items-center gap-2 rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-cream-100 px-6 py-3 text-sm transition-all hover:-translate-y-0.5"
            >
              {i18n.register}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => onOpen(ev)}
              className="inline-flex items-center gap-2 rounded-full border border-cream-100/30 hover:bg-cream-100 hover:text-ink px-6 py-3 text-sm transition-all"
            >
              {i18n.viewDetails}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {count > 1 && (
        <>
          <button onClick={() => go(-1)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cream-100/85 hover:bg-cream-100 text-ink flex items-center justify-center" aria-label="prev" data-testid="slider-prev">
            <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button onClick={() => go(1)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cream-100/85 hover:bg-cream-100 text-ink flex items-center justify-center" aria-label="next" data-testid="slider-next">
            <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-terracotta-400" : "w-1.5 bg-cream-100/40"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EventDetailModal({ ev, lang, onClose, i18n }) {
  const Icon = typeIcon(ev.type);
  const [view, setView] = useState("details"); // 'details' | 'register' | 'success' | 'waitlist-success'
  return (
    <div
      data-testid="event-detail-modal"
      className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-cream-100 rounded-3xl shadow-float w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        {ev.image && view === "details" && (
          <div className="aspect-[16/8] overflow-hidden rounded-t-3xl">
            <img src={ev.image} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-7 md:p-10 relative">
          <button onClick={onClose} className="absolute top-5 right-5 text-ink/40 hover:text-ink" aria-label="close" data-testid="event-modal-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>

          {view === "details" && (
            <>
              <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-medium px-2.5 py-1 rounded-full ${typeTone(ev.type)}`}>
                <Icon className="w-3 h-3" strokeWidth={1.6} />
                {typeLabel(ev.type, lang)}
              </span>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl leading-tight tracking-tight text-ink">{pickLang(ev.title, lang)}</h2>

              <dl className="mt-6 grid sm:grid-cols-2 gap-4 text-sm text-ink/75">
                <InfoRow icon={Calendar}>{formatDateTime(ev.date, lang)}{ev.end_date ? ` → ${formatDate(ev.end_date, lang)}` : ""}</InfoRow>
                {pickLang(ev.location, lang) && <InfoRow icon={MapPin}>{pickLang(ev.location, lang)}</InfoRow>}
                {ev.capacity != null && <InfoRow icon={Users}>{ev.capacity} {i18n.capacity}</InfoRow>}
                {pickLang(ev.price, lang) && <InfoRow icon={null} label={i18n.priceLabel}>{pickLang(ev.price, lang)}</InfoRow>}
                {ev.location_url && (
                  <InfoRow icon={LinkIcon}>
                    <a href={ev.location_url} target="_blank" rel="noreferrer" className="text-terracotta-400 hover:underline">
                      {ev.location_url}
                    </a>
                  </InfoRow>
                )}
              </dl>

              {pickLang(ev.body, lang) && (
                <div className="mt-7 text-base text-ink/80 font-light leading-relaxed whitespace-pre-wrap">
                  {pickLang(ev.body, lang)}
                </div>
              )}
              {!pickLang(ev.body, lang) && pickLang(ev.description, lang) && (
                <p className="mt-7 text-base text-ink/80 font-light leading-relaxed">{pickLang(ev.description, lang)}</p>
              )}

              <div className="mt-9 flex flex-wrap gap-3">
                <button
                  onClick={() => setView("register")}
                  data-testid="event-register-open"
                  className="btn-accent"
                >
                  {i18n.register}
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
                {ev.registration_url && (
                  <a
                    href={ev.registration_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="event-register-external"
                    className="btn-secondary"
                  >
                    {i18n.externalLink}
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                )}
                <button onClick={onClose} className="btn-secondary" data-testid="event-modal-close-btn">
                  {i18n.close}
                </button>
              </div>
            </>
          )}

          {view === "register" && (
            <RegistrationForm
              ev={ev}
              lang={lang}
              i18n={i18n}
              onBack={() => setView("details")}
              onSuccess={(kind) => setView(kind === "waitlist" ? "waitlist-success" : "success")}
            />
          )}

          {view === "success" && (
            <SuccessView title={i18n.successTitle} body={i18n.successBody} icon={Calendar} onClose={onClose} i18n={i18n} testid="event-register-success" ev={ev} lang={lang} />
          )}

          {view === "waitlist-success" && (
            <SuccessView title={i18n.waitlistSuccessTitle} body={i18n.waitlistSuccessBody} icon={Users} onClose={onClose} i18n={i18n} testid="event-waitlist-success" ev={null} lang={lang} />
          )}
        </div>
      </div>
    </div>
  );
}

function buildGoogleCalendarUrl(ev, lang) {
  const title = encodeURIComponent((ev.title?.[lang] || ev.title?.tr || ev.title?.en || "Event"));
  const desc = encodeURIComponent(ev.description?.[lang] || ev.description?.tr || ev.description?.en || "");
  const loc = encodeURIComponent(ev.location?.[lang] || ev.location?.tr || ev.location?.en || ev.location_url || "");
  const toGCal = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  };
  const start = toGCal(ev.date);
  const end = toGCal(ev.end_date || new Date(new Date(ev.date).getTime() + 3600000));
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${desc}&location=${loc}`;
}

function buildIcsUrl(ev, lang) {
  const title = ev.title?.[lang] || ev.title?.tr || ev.title?.en || "Event";
  const desc = ev.description?.[lang] || ev.description?.tr || ev.description?.en || "";
  const loc = ev.location?.[lang] || ev.location?.tr || ev.location?.en || ev.location_url || "";
  const toIcs = (d) => {
    if (!d) return "";
    return new Date(d).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  };
  const start = toIcs(ev.date);
  const end = toIcs(ev.end_date || new Date(new Date(ev.date).getTime() + 3600000));
  const esc = (s) => (s || "").replace(/,/g, "\\,").replace(/\n/g, "\\n");
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//inTouch//Events//TR",
    "BEGIN:VEVENT",
    `DTSTART:${start}`, `DTEND:${end}`,
    `SUMMARY:${esc(title)}`,
    `DESCRIPTION:${esc(desc)}`,
    `LOCATION:${esc(loc)}`,
    "STATUS:CONFIRMED", "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  return URL.createObjectURL(blob);
}

function SuccessView({ title, body, icon: Icon, onClose, i18n, testid, ev, lang }) {
  const gcUrl = ev ? buildGoogleCalendarUrl(ev, lang) : null;
  const [icsUrl, setIcsUrl] = React.useState(null);

  React.useEffect(() => {
    if (!ev) return;
    const url = buildIcsUrl(ev, lang);
    setIcsUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [ev, lang]);

  return (
    <div className="text-center py-6" data-testid={testid}>
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-sage-100 text-sage-700 mb-4">
        <Icon className="w-6 h-6" strokeWidth={1.4} />
      </div>
      <h3 className="font-serif text-3xl text-ink mb-3">{title}</h3>
      <p className="text-base text-ink/70 font-light max-w-md mx-auto leading-relaxed whitespace-pre-line">{body}</p>

      {ev && (
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {gcUrl && (
            <a
              href={gcUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#4285F4] hover:bg-[#3367D6] text-white px-5 py-2.5 text-sm transition-all hover:-translate-y-0.5"
            >
              <CalendarPlus className="w-4 h-4" strokeWidth={1.5} />
              Google Calendar
            </a>
          )}
          {icsUrl && (
            <a
              href={icsUrl}
              download="intouch-event.ics"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 hover:bg-cream-200 text-ink px-5 py-2.5 text-sm transition-all hover:-translate-y-0.5"
            >
              <CalendarPlus className="w-4 h-4" strokeWidth={1.5} />
              Apple / Outlook (.ics)
            </a>
          )}
        </div>
      )}

      <button onClick={onClose} className="btn-primary mt-6">{i18n.close}</button>
    </div>
  );
}

function RegistrationForm({ ev, lang, i18n, onBack, onSuccess }) {
  const [form, setForm] = React.useState({ name: "", surname: "", email: "", phone: "", guests: 1, notes: "", address: "", city: "" });
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState("");
  const [checkoutHtml, setCheckoutHtml] = React.useState(null); // iyzico form HTML

  const isPaid = getPriceKurus(ev.price) > 0;
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) return;
    setStatus("sending");

    try {
      if (isPaid) {
        // Ücretli → iyzico checkout
        const res = await axios.post(`${API}/events/${ev.id}/checkout`, {
          name: form.name,
          surname: form.surname || form.name,
          email: form.email,
          phone: form.phone || null,
          guests: Number(form.guests) || 1,
          notes: form.notes || null,
          address: form.address || null,
          city: form.city || null,
        });
        setCheckoutHtml(res.data.checkoutFormContent);
        setStatus("checkout");
      } else {
        // Ücretsiz → mevcut kayıt akışı
        await axios.post(`${API}/events/${ev.id}/register`, {
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          guests: Number(form.guests) || 1,
          notes: form.notes || null,
        });
        onSuccess("registered");
      }
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : null;
      if (code === "capacity_full" && !isPaid) {
        try {
          await axios.post(`${API}/events/${ev.id}/waitlist`, {
            name: form.name,
            email: form.email,
            phone: form.phone || null,
            guests: Number(form.guests) || 1,
            notes: form.notes || null,
          });
          onSuccess("waitlist");
          return;
        } catch (e3) {
          const detail2 = e3?.response?.data?.detail;
          const msg2 = typeof detail2 === "object" ? detail2.message : (typeof detail2 === "string" ? detail2 : i18n.errorGeneric);
          setError(msg2);
        }
      } else {
        const msg = typeof detail === "object" ? detail.message : (typeof detail === "string" ? detail : i18n.errorGeneric);
        setError(msg);
      }
      setStatus("error");
    } finally {
      if (status !== "success" && status !== "checkout") setStatus("idle");
    }
  };

  // iyzico checkout iframe görünümü
  if (status === "checkout" && checkoutHtml) {
    return (
      <div data-testid="iyzico-checkout">
        <div className="mb-4">
          <button type="button" onClick={() => { setStatus("idle"); setCheckoutHtml(null); }} className="text-xs uppercase tracking-[0.22em] text-ink/55 hover:text-ink">← {i18n.back}</button>
        </div>
        <h3 className="font-serif text-2xl text-ink mb-4">{i18n.paymentTitle || "Ödeme"}</h3>
        <div
          id="iyzipay-checkout-form"
          className="responsive"
          dangerouslySetInnerHTML={{ __html: checkoutHtml }}
        />
        <p className="text-xs text-ink/45 mt-4 text-center">{i18n.paymentNote || "Ödeme iyzico güvencesiyle işlenmektedir."}</p>
      </div>
    );
  }

  return (
    <form data-testid="event-register-form" onSubmit={submit} className="space-y-4">
      <div>
        <button type="button" onClick={onBack} className="text-xs uppercase tracking-[0.22em] text-ink/55 hover:text-ink">← {i18n.back}</button>
      </div>
      <h3 className="font-serif text-3xl text-ink">{i18n.formTitle}</h3>
      <p className="text-sm text-ink/65 font-light">{pickLang(ev.title, lang)}</p>

      {isPaid && (
        <div className="flex items-center gap-2 bg-terracotta-50 border border-terracotta-100 rounded-xl px-4 py-3">
          <span className="text-sm font-medium text-terracotta-500">{i18n.priceLabel}: {pickLang(ev.price, lang)}</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <RegField label={i18n.fName} required>
          <input data-testid="reg-name" value={form.name} onChange={update("name")} required className="reg-input" />
        </RegField>
        {isPaid && (
          <RegField label={i18n.fSurname || "Soyad"} required>
            <input data-testid="reg-surname" value={form.surname} onChange={update("surname")} required className="reg-input" />
          </RegField>
        )}
        <RegField label={i18n.fEmail} required>
          <input data-testid="reg-email" type="email" value={form.email} onChange={update("email")} required className="reg-input" />
        </RegField>
        <RegField label={i18n.fPhone}>
          <input data-testid="reg-phone" type="tel" value={form.phone} onChange={update("phone")} className="reg-input" />
        </RegField>
        <RegField label={i18n.fGuests}>
          <input data-testid="reg-guests" type="number" min="1" max="20" value={form.guests} onChange={update("guests")} className="reg-input" />
        </RegField>
        {isPaid && (
          <RegField label={i18n.fCity || "Şehir"}>
            <input data-testid="reg-city" value={form.city} onChange={update("city")} className="reg-input" />
          </RegField>
        )}
      </div>
      <RegField label={i18n.fNotes}>
        <textarea data-testid="reg-notes" rows={3} value={form.notes} onChange={update("notes")} className="reg-input resize-none" />
      </RegField>

      {error && (
        <div data-testid="reg-error" className="text-sm text-terracotta-500 bg-terracotta-50 border border-terracotta-100 rounded-xl p-3">{error}</div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onBack} className="btn-secondary">{i18n.cancel}</button>
        <button type="submit" disabled={status === "sending"} data-testid="reg-submit" className="btn-accent disabled:opacity-60">
          {status === "sending" ? i18n.sending : isPaid ? (i18n.proceedToPayment || "Ödemeye Geç") : i18n.confirm}
          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      <style>{`
        .reg-input { width:100%;background:#FBFAF6;border:1px solid #E5E0D8;border-radius:0.85rem;padding:0.75rem 0.95rem;font-family:'Outfit',sans-serif;font-size:0.95rem;color:#2C3531;font-weight:300;transition:all 0.2s ease; }
        .reg-input:focus { outline:none;border-color:#839788;background:#FFFFFF;box-shadow:0 0 0 4px rgba(131,151,136,0.12); }
      `}</style>
    </form>
  );
}

function getPriceKurus(priceField) {
  if (!priceField) return 0;
  const raw = priceField?.tr || priceField?.en || "";
  if (!raw) return 0;
  const nums = raw.match(/[\d]+(?:[.,][\d]+)?/);
  if (!nums) return 0;
  try { return Math.round(parseFloat(nums[0].replace(",", ".")) * 100); } catch { return 0; }
}

function RegField({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.22em] text-ink/55 font-medium mb-2">
        {label}{required && <span className="text-terracotta-400">*</span>}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      {Icon ? <Icon className="w-4 h-4 mt-1 text-terracotta-400 shrink-0" strokeWidth={1.5} /> : <span className="text-xs uppercase tracking-widest text-ink/45 mt-1">{label}:</span>}
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}

const TR = {
  eyebrow: "Etkinlikler",
  title: "Birlikte öğrenmek, paylaşmak ve nefes almak için fırsatlar",
  lead: "Zoom eğitimleri, açık hava buluşmaları, atölyeler ve daha fazlası — yaklaşan etkinliklerimize katılın.",
  all: "Tümü",
  upcoming: "Yaklaşan",
  past: "Geçmiş",
  viewDetails: "Detayları gör",
  register: "Hemen Kayıt Ol",
  externalLink: "Harici kayıt",
  close: "Kapat",
  back: "Geri",
  cancel: "İptal",
  confirm: "Kaydımı Onayla",
  sending: "Gönderiliyor…",
  capacity: "kişi kontenjan",
  priceLabel: "Ücret",
  pastBadge: "Geçmiş",
  loading: "Yükleniyor…",
  emptyTitle: "Yakında yeni etkinlikler.",
  emptyBody: "Şu an için seçilen filtreye uyan bir etkinlik yok.",
  formTitle: "Etkinliğe Kayıt",
  fName: "Ad Soyad",
  fEmail: "E-posta",
  fPhone: "Telefon (opsiyonel)",
  fGuests: "Katılımcı sayısı",
  fNotes: "Notunuz (opsiyonel)",
  successTitle: "Kaydınız alındı.",
  successBody: "Onay e-postası ve takvim eki (.ics) az önce gönderildi. Etkinliği tek tıkla kendi takviminize ekleyebilirsiniz.",
  waitlistSuccessTitle: "Bekleme listesindesiniz.",
  waitlistSuccessBody: "Etkinlik dolu olduğu için sizi bekleme listesine aldık. Yer açılırsa size öncelikli olarak haber vereceğiz.",
  list: "Liste",
  calendar: "Takvim",
  errorGeneric: "Bir hata oluştu. Lütfen tekrar deneyin.",
  fSurname: "Soyad",
  fCity: "Şehir",
  proceedToPayment: "Ödemeye Geç",
  paymentTitle: "Güvenli Ödeme",
  paymentNote: "Ödeme iyzico güvencesiyle işlenmektedir.",
};

const EN = {
  eyebrow: "Events",
  title: "Opportunities to learn, share and breathe together",
  lead: "Zoom trainings, outdoor gatherings, workshops, and more — join our upcoming events.",
  all: "All",
  upcoming: "Upcoming",
  past: "Past",
  viewDetails: "View details",
  register: "Register Now",
  externalLink: "External link",
  close: "Close",
  back: "Back",
  cancel: "Cancel",
  confirm: "Confirm Registration",
  sending: "Sending…",
  capacity: "spots",
  priceLabel: "Price",
  pastBadge: "Past",
  loading: "Loading…",
  emptyTitle: "New events coming soon.",
  emptyBody: "No events match the current filter right now.",
  formTitle: "Register for Event",
  fName: "Full name",
  fEmail: "Email",
  fPhone: "Phone (optional)",
  fGuests: "Number of attendees",
  fNotes: "Your note (optional)",
  successTitle: "Your registration is in.",
  successBody: "A confirmation email with a calendar attachment (.ics) has just been sent. Add the event to your calendar with one click.",
  waitlistSuccessTitle: "You're on the waitlist.",
  waitlistSuccessBody: "This event is full — we've added you to the waitlist. If a seat opens up, you'll be the first to know.",
  list: "List",
  calendar: "Calendar",
  errorGeneric: "Something went wrong. Please try again.",
  fSurname: "Surname",
  fCity: "City",
  proceedToPayment: "Proceed to Payment",
  paymentTitle: "Secure Payment",
  paymentNote: "Payment is processed securely via iyzico.",
};
